import { Routes } from '@angular/router';
import { environment } from '../environments/environment';
import { authGuard, portalMatch, publicOnlyGuard } from './core/auth/auth.guard';
import { OWNER_STATISTICS_PROVIDERS } from './core/owner/owner-statistics.providers';
import { crmAccessGuard, noAccessOnlyGuard, noPlanOnlyGuard, permissionGuard, planChildGuard, planGuard, settingsGuard, superAdminGuard, automationHomeGuard, allPermissionsGuard } from './core/auth/permission.guard';
import { SETTINGS_ANY_PERMISSION } from './features/settings/settings-access';

const crmRoutes: Routes = [
  {
    path: 'login',
    title: 'Sign in — Metasmile',
    canActivate: [publicOnlyGuard],
    loadComponent: () => import('./features/auth/login/login').then((m) => m.Login),
  },
  {
    path: 'forgot-password',
    title: 'Forgot password — Metasmile',
    loadComponent: () =>
      import('./features/auth/forgot-password/forgot-password').then((m) => m.ForgotPassword),
  },
  {
    // Unguarded on purpose: the recovery link's session may not be established yet when
    // the route activates, and the component itself shows an "expired link" state if it
    // never materializes — a guard would just redirect that case straight to /login.
    path: 'reset-password',
    title: 'Reset password — Metasmile',
    loadComponent: () =>
      import('./features/auth/reset-password/reset-password').then((m) => m.ResetPassword),
  },
  {
    // Public, unauthenticated campaign landing page (BRD §5.2 AT-5).
    path: 'l/:slug',
    title: 'Metasmile',
    loadComponent: () => import('./features/landing/landing').then((m) => m.Landing),
  },
  {
    // Public, unauthenticated form-fill page — the tables it reads/writes
    // (public.form / questions_forms / answers_tb / responses_tb /
    // form_links_tb) are already anon-accessible by design (shared with the
    // sibling clinic app), so this calls them directly rather than through an
    // Edge Function.
    path: 'form/:code',
    title: 'Metasmile',
    loadComponent: () => import('./features/public-form/public-form').then((m) => m.PublicForm),
  },
  {
    // Public, unauthenticated opt-out page behind {{link.unsubscribe}}.
    //
    // It lives here rather than in the `unsubscribe` Edge Function that owns the
    // token because Supabase rewrites text/html to text/plain on
    // *.supabase.co functions — the function's page arrived as raw source with
    // an inert form button, so a patient could read it and still not opt out.
    // The function remains the API and the RFC 8058 one-click target.
    //
    // No guard, deliberately: the recipient has no session and never will. The
    // opaque token in the URL is the entire credential.
    path: 'unsubscribe',
    title: 'Metasmile',
    loadComponent: () =>
      import('./features/public-unsubscribe/public-unsubscribe').then((m) => m.PublicUnsubscribe),
  },
  {
    // Public, unauthenticated booking page behind {{link.booking}}.
    //
    // Here rather than in the `book` Edge Function for exactly the reason the
    // unsubscribe route above gives: Supabase rewrites text/html to text/plain
    // on *.supabase.co, so the function's page arrived as raw source with inert
    // slot buttons. It only ever looked right in prod because that reaches it
    // through a branded custom domain; Dev has none. The function stays the
    // JSON API and the holder of the service_role RPCs, and 302s browsers here.
    //
    // No guard: the patient has no session and never will. The opaque token in
    // the query string is the entire credential.
    path: 'book',
    title: 'Metasmile',
    data: { mode: 'book' },
    loadComponent: () =>
      import('./features/public-booking/public-booking').then((m) => m.PublicBooking),
  },
  {
    // Same component, same reasoning — this one moves an existing appointment
    // rather than creating one. The mode picks the API and the copy.
    path: 'reschedule',
    title: 'Metasmile',
    data: { mode: 'reschedule' },
    loadComponent: () =>
      import('./features/public-booking/public-booking').then((m) => m.PublicBooking),
  },
  {
    // Same component again, and the same text/html reasoning — but this link is
    // NOT personalised. Its credential is `?l=<slug>`, a crm.public_booking_links
    // row belonging to a BRANCH: printed on a poster, stuck in an Instagram bio,
    // behind a QR code at reception. One slug, every passer-by.
    //
    // So nobody is identified by it, and the flow inverts: the visitor picks a
    // slot and only then enters a name and a phone, which the `book-open`
    // function turns into a LEAD. Nothing it does can reach an existing
    // patient's record, and nothing it does writes the diary — an open-link
    // booking always lands in the clinic's approval queue.
    //
    // No guard, for the same reason as the two routes above and one more: there
    // is no session to guard and never will be. The slug in the query string is
    // the entire credential, it names no clinic, no patient and no doctor, and
    // the function re-derives everything from it server-side.
    path: 'book-open',
    title: 'Metasmile',
    data: { mode: 'open' },
    loadComponent: () =>
      import('./features/public-booking/public-booking').then((m) => m.PublicBooking),
  },
  {
    // Public, unauthenticated page behind {{link.feedback}}: the patient rates an
    // attended visit. The clinic app's own /feedback/:code over the same code
    // and the same two anon-granted RPCs, so no Edge Function sits between them
    // and no table is read.
    //
    // No guard: the patient has no session and never will. `?t=<code>` is the
    // entire credential and identifies one appointment.
    path: 'feedback',
    title: 'Metasmile',
    loadComponent: () =>
      import('./features/public-feedback/public-feedback').then((m) => m.PublicFeedback),
  },
  {
    // Public, unauthenticated page behind {{appointment.add_to_calendar}}: one
    // appointment, with a button per calendar. The `calendar` Edge Function is
    // its API and also sends its own .ics links here from an iPhone browser
    // that is not Safari — WhatsApp's built-in one shows the file as raw text.
    //
    // No guard: the patient has no session and never will. `?t=` is the
    // read-only calendar token, the entire credential.
    path: 'add-to-calendar',
    title: 'Metasmile',
    loadComponent: () =>
      import('./features/public-add-to-calendar/public-add-to-calendar').then((m) => m.PublicAddToCalendar),
  },
  {
    // Signed in but no CRM permissions yet (access granted, no role assigned).
    path: 'no-access',
    title: 'No access — Metasmile',
    canActivate: [authGuard, noAccessOnlyGuard],
    loadComponent: () => import('./features/auth/no-access/no-access').then((m) => m.NoAccess),
  },
  {
    // Signed in, has CRM access, but the clinic's CRM subscription is missing,
    // lapsed or could not be checked. Owner and staff see different content.
    path: 'plans',
    title: 'Subscription — Metasmile',
    canActivate: [authGuard, noPlanOnlyGuard],
    loadComponent: () => import('./features/no-plan/no-plan').then((m) => m.NoPlan),
  },
  {
    path: '',
    // planGuard before crmAccessGuard: a clinic with no active plan now gets an
    // empty permission set from my_permissions(), so it must be sent to /plans
    // (planGuard) before crmAccessGuard would otherwise divert it to /no-access.
    canActivate: [authGuard, planGuard, crmAccessGuard],
    // And again for every page inside: canActivate above runs only when the shell
    // is entered, not on navigation between its children.
    canActivateChild: [planChildGuard],
    loadComponent: () => import('./layout/shell/shell').then((m) => m.Shell),
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'dashboard' },
      {
        path: 'dashboard',
        title: 'Dashboard',
        loadComponent: () => import('./features/dashboard/dashboard').then((m) => m.Dashboard),
      },
      {
        path: 'calendar',
        title: 'Calendar',
        canActivate: [permissionGuard(['calendar.read'])],
        loadComponent: () =>
          import('./features/calendar/calendar-page/calendar-page').then((m) => m.CalendarPage),
      },
      {
        path: 'follow-ups',
        title: 'Follow-ups',
        canActivate: [permissionGuard(['followups.read'])],
        loadComponent: () =>
          import('./features/follow-ups/follow-ups-list/follow-ups-list').then((m) => m.FollowUpsList),
      },
      {
        // Unguarded on purpose: crm.notifications' RLS scopes rows to the recipient, so
        // "my notifications" needs no permission key — every signed-in user has a feed.
        path: 'notifications',
        title: 'Notifications',
        loadComponent: () =>
          import('./features/notifications/notifications-page/notifications-page').then(
            (m) => m.NotificationsPage,
          ),
      },
      {
        // Unguarded for the SAME reason as the feed above, and that is the whole
        // point of this route existing. These preferences used to live in a tab
        // on /settings, whose guard requires one of twelve admin permission keys
        // — so the one screen every user needs was reachable only by admins.
        // Personal preferences belong beside the notifications they control.
        // The admin-only "clinic defaults" half is gated inside the component by
        // crm.can_manage_notification_defaults(), not by this route.
        path: 'notifications/settings',
        title: 'Notification settings',
        loadComponent: () =>
          import('./features/notifications/notification-settings-page/notification-settings-page').then(
            (m) => m.NotificationSettingsPage,
          ),
      },
      {
        path: 'contacts',
        title: 'Contacts',
        loadComponent: () => import('./features/contacts/contacts').then((m) => m.Contacts),
      },
      {
        // Unguarded, like /contacts — patient data isn't RBAC'd by a dedicated permission
        // namespace in this app. Follow-up CRUD on this page is gated by followups.* instead.
        path: 'patients/:id',
        title: 'Patient',
        loadComponent: () =>
          import('./features/contacts/patient-detail/patient-detail').then((m) => m.PatientDetail),
      },
      {
        path: 'leads',
        title: 'Leads',
        canActivate: [permissionGuard(['leads.read'])],
        loadComponent: () =>
          import('./features/leads/leads-list/leads-list').then((m) => m.LeadsList),
      },
      {
        path: 'leads/:id',
        title: 'Lead',
        canActivate: [permissionGuard(['leads.read'])],
        loadComponent: () =>
          import('./features/leads/lead-detail/lead-detail').then((m) => m.LeadDetail),
      },
      {
        path: 'lists',
        title: 'Lists',
        canActivate: [permissionGuard(['lists.read'])],
        loadComponent: () =>
          import('./features/lists/lists-index/lists-index').then((m) => m.ListsIndex),
      },
      {
        path: 'forms',
        title: 'Forms',
        canActivate: [permissionGuard(['forms.read'])],
        loadComponent: () =>
          import('./features/forms/forms-list/forms-list').then((m) => m.FormsList),
      },
      {
        path: 'lists/static/:id',
        title: 'List',
        canActivate: [permissionGuard(['lists.read'])],
        loadComponent: () =>
          import('./features/lists/static-list-detail/static-list-detail').then(
            (m) => m.StaticListDetail,
          ),
      },
      {
        path: 'lists/smart/:id',
        title: 'Smart List',
        canActivate: [permissionGuard(['lists.read'])],
        loadComponent: () =>
          import('./features/lists/smart-list-detail/smart-list-detail').then(
            (m) => m.SmartListDetail,
          ),
      },
      {
        path: 'communications',
        title: 'Communications',
        canActivate: [permissionGuard(['communications.read'])],
        loadComponent: () =>
          import('./features/communications/communications').then((m) => m.Communications),
      },
      {
        path: 'calls',
        title: 'Call History',
        canActivate: [permissionGuard(['telephony.read'])],
        loadComponent: () => import('./features/calls/call-history').then((m) => m.CallHistory),
      },
      {
        path: 'communications/templates',
        title: 'Message Templates',
        canActivate: [permissionGuard(['templates.read'])],
        loadComponent: () =>
          import('./features/communications/templates/templates').then(
            (m) => m.MessageTemplatesPage,
          ),
      },
      {
        // Block designer for one template. A child of the (flat) templates route
        // rather than a tab inside it: the designer needs the full viewport.
        path: 'communications/templates/:id/design',
        title: 'Email Designer',
        canActivate: [permissionGuard(['templates.read'])],
        loadComponent: () =>
          import('./features/communications/template-designer/template-designer').then(
            (m) => m.TemplateDesigner,
          ),
      },
      {
        // The GHL-style workflow builder IS automation now. The old
        // single-trigger/single-action rules still run on the server via
        // automation-runner until the cutover migration migrates them, but they
        // are no longer editable here — see the cutover phase.
        path: 'automation',
        title: 'Automation',
        canActivate: [automationHomeGuard],
        loadComponent: () =>
          import('./features/automation/workflow-list/workflow-list').then((m) => m.WorkflowList),
      },
      {
        // A workflow opens by its name (workflow-link.ts), under its own
        // segment so no name can collide with the broadcasts or analytics tabs.
        path: 'automation/workflows/:name/runs',
        title: 'Workflow runs',
        canActivate: [permissionGuard(['automation.read'])],
        loadComponent: () =>
          import('./features/automation/workflow-runs/workflow-runs').then((m) => m.WorkflowRuns),
      },
      {
        // Read permission opens the canvas; every mutation inside it is gated
        // separately on automation.update / automation.publish, and the server
        // re-checks both regardless.
        path: 'automation/workflows/:name',
        title: 'Workflow',
        canActivate: [permissionGuard(['automation.read'])],
        loadComponent: () =>
          import('./features/automation/workflow-canvas/workflow-canvas').then(
            (m) => m.WorkflowCanvas,
          ),
      },
      {
        // Broadcasts: a different engine (one message to a frozen audience, no
        // branching) living under the same Automation nav entry as a tab — see
        // automation-tabs.ts. Declared BEFORE the old automation/:id links
        // below, which would otherwise swallow 'broadcasts' as an id.
        path: 'automation/broadcasts',
        title: 'Broadcasts',
        canActivate: [permissionGuard(['broadcasts.read'])],
        loadComponent: () =>
          import('./features/automation/broadcast-list/broadcast-list').then(
            (m) => m.BroadcastList,
          ),
      },
      {
        // Combined view over both engines. Same before-bare-:id ordering rule.
        path: 'automation/analytics',
        title: 'Automation analytics',
        canActivate: [permissionGuard(['automation.read'])],
        loadComponent: () =>
          import('./features/automation/automation-analytics/automation-analytics').then(
            (m) => m.AutomationAnalyticsPage,
          ),
      },
      {
        path: 'automation/broadcasts/:id/report',
        title: 'Broadcast report',
        canActivate: [permissionGuard(['broadcasts.read'])],
        loadComponent: () =>
          import('./features/automation/broadcast-report/broadcast-report').then(
            (m) => m.BroadcastReport,
          ),
      },
      {
        path: 'automation/broadcasts/:id',
        title: 'Broadcast',
        canActivate: [permissionGuard(['broadcasts.read'])],
        loadComponent: () =>
          import('./features/automation/broadcast-compose/broadcast-compose').then(
            (m) => m.BroadcastCompose,
          ),
      },
      // Old links by workflow id (bookmarks, history) go to the list. Declared
      // AFTER automation/broadcasts* and automation/analytics, which a bare :id
      // would otherwise swallow.
      { path: 'automation/:id/runs', redirectTo: '/automation' },
      { path: 'automation/:id', redirectTo: '/automation' },
      {
        path: 'opportunities',
        title: 'Opportunities',
        canActivate: [permissionGuard(['opportunities.read'])],
        loadComponent: () =>
          import('./features/opportunities/opportunities-board/opportunities-board').then(
            (m) => m.OpportunitiesBoard,
          ),
      },
      {
        path: 'opportunities/:id',
        title: 'Opportunity',
        canActivate: [permissionGuard(['opportunities.read'])],
        loadComponent: () =>
          import('./features/opportunities/opportunity-detail/opportunity-detail').then(
            (m) => m.OpportunityDetail,
          ),
      },
      { path: 'deals', redirectTo: 'opportunities' },
      // Campaigns: the budget-and-attribution wrapper. `crm.campaigns` is the
      // root of the attribution tree (leads, ads, landing pages, QR codes all
      // reference it) and has had a service since that work landed but never a
      // UI — this is that UI. It owns no content and no send button; the sends
      // live under Automation and are untouched.
      {
        path: 'campaigns',
        title: 'Campaigns',
        canActivate: [permissionGuard(['campaigns.read'])],
        loadComponent: () =>
          import('./features/campaigns/campaign-list/campaign-list').then((m) => m.CampaignList),
      },
      {
        // The same templates screen Communications renders, inside the
        // Campaigns tab bar. Composition, not a copy — see
        // campaign-templates.ts. Declared before campaigns/:id so the bare :id
        // cannot match 'templates' as an id.
        path: 'campaigns/templates',
        title: 'Templates',
        canActivate: [permissionGuard(['templates.read'])],
        loadComponent: () =>
          import('./features/campaigns/campaign-templates/campaign-templates').then(
            (m) => m.CampaignTemplates,
          ),
      },
      {
        // Before campaigns/:id, same reason as its neighbours: a bare :id
        // would match 'statistics' as an id.
        path: 'campaigns/statistics',
        title: 'Campaign statistics',
        canActivate: [permissionGuard(['campaigns.read'])],
        loadComponent: () =>
          import('./features/campaigns/campaign-statistics/campaign-statistics').then(
            (m) => m.CampaignStatistics,
          ),
      },
      {
        // Declared before campaigns/:id for the same reason campaigns/new is:
        // a bare :id would match 'trigger-links' as an id.
        path: 'campaigns/trigger-links',
        title: 'Trigger links',
        canActivate: [permissionGuard(['campaigns.read'])],
        loadComponent: () =>
          import('./features/campaigns/trigger-links/trigger-links').then((m) => m.TriggerLinks),
      },
      {
        // The per-link click report, reached from the Clicks column. Must sit
        // AFTER the bare trigger-links path above and BEFORE campaigns/:id —
        // otherwise 'trigger-links' is read as a campaign id.
        //
        // :code is the Shlink SHORT CODE, not the row id. A sequential id in a
        // URL is enumerable, and this is a multi-tenant system — the code is 8
        // random base62 characters and reveals nothing about how many links
        // exist or in what order. Authorisation is still RLS, not the URL.
        path: 'campaigns/trigger-links/:code',
        title: 'Trigger link report',
        canActivate: [permissionGuard(['campaigns.read'])],
        loadComponent: () =>
          import('./features/campaigns/trigger-link-report/trigger-link-report').then(
            (m) => m.TriggerLinkReport,
          ),
      },
      {
        // Building a campaign is two steps: content, then setup. Both are real
        // URLs so a half-built campaign can be linked to and reloaded.
        //
        // Declared BEFORE the bare campaigns/:id below — the same trap
        // automation/:id documents: a bare :id would match 'new' as an id and
        // send the user to a detail page for campaign NaN. The content step
        // serves both create and edit, telling them apart by whether :id is
        // present.
        path: 'campaigns/new',
        title: 'New campaign',
        canActivate: [permissionGuard(['campaigns.create'])],
        loadComponent: () =>
          import('./features/campaigns/campaign-content/campaign-content').then(
            (m) => m.CampaignContent,
          ),
      },
      {
        path: 'campaigns/:id/content',
        title: 'Campaign content',
        canActivate: [permissionGuard(['campaigns.update'])],
        loadComponent: () =>
          import('./features/campaigns/campaign-content/campaign-content').then(
            (m) => m.CampaignContent,
          ),
      },
      {
        path: 'campaigns/:id/setup',
        title: 'Campaign setup',
        canActivate: [permissionGuard(['campaigns.update'])],
        loadComponent: () =>
          import('./features/campaigns/campaign-setup/campaign-setup').then((m) => m.CampaignSetup),
      },
      {
        // Before the bare campaigns/:id, same ordering trap as the steps.
        path: 'campaigns/:id/report',
        title: 'Campaign report',
        canActivate: [permissionGuard(['campaigns.read'])],
        loadComponent: () =>
          import('./features/campaigns/campaign-report/campaign-report').then(
            (m) => m.CampaignReport,
          ),
      },
      {
        // Before the bare campaigns/:id, the same trap the two routes above
        // are ordered around: otherwise ':id' swallows 'review'.
        path: 'campaigns/:id/review',
        title: 'Campaign review',
        // Sending is the irreversible act, so this needs send rights on top of
        // the update rights the earlier steps ask for — BOTH (permissionGuard
        // is any-of, which let campaigns.update alone through).
        canActivate: [allPermissionsGuard(['campaigns.update', 'broadcasts.send'])],
        loadComponent: () =>
          import('./features/campaigns/campaign-review/campaign-review').then(
            (m) => m.CampaignReview,
          ),
      },
      {
        path: 'campaigns/:id',
        title: 'Campaign',
        canActivate: [permissionGuard(['campaigns.read'])],
        loadComponent: () =>
          import('./features/campaigns/campaign-detail/campaign-detail').then(
            (m) => m.CampaignDetail,
          ),
      },
      {
        path: 'attribution',
        title: 'Attribution & ROI',
        canActivate: [permissionGuard(['attribution.read'])],
        loadComponent: () =>
          import('./features/attribution/attribution-dashboard/attribution-dashboard').then(
            (m) => m.AttributionDashboard,
          ),
      },
      {
        path: 'settings',
        title: 'Settings',
        canActivate: [
          // Same list the sidebar and the page use. Hand-maintained here it had
          // already fallen behind — telephony was missing, so a user whose only
          // settings access was their own extension hit a blocked route. The
          // clinic owner is let in regardless, for the Plan tab.
          settingsGuard([...SETTINGS_ANY_PERMISSION]),
        ],
        loadComponent: () => import('./features/settings/settings').then((m) => m.Settings),
      },
    ],
  },
  { path: '**', redirectTo: '' },
];

const adminRoutes: Routes = [
  {
    path: 'login',
    title: 'Sign in — Metasmile Admin',
    canActivate: [publicOnlyGuard],
    loadComponent: () => import('./features/auth/login/login').then((m) => m.Login),
  },
  {
    path: 'forgot-password',
    title: 'Forgot password — Metasmile Admin',
    loadComponent: () =>
      import('./features/auth/forgot-password/forgot-password').then((m) => m.ForgotPassword),
  },
  {
    path: 'reset-password',
    title: 'Reset password — Metasmile Admin',
    loadComponent: () =>
      import('./features/auth/reset-password/reset-password').then((m) => m.ResetPassword),
  },
  // Two portals share one URL space; portalMatch picks the shell per session.
  // SuperAdmin — platform role holders (wins when a user is also an owner).
  {
    path: '',
    canMatch: [portalMatch('superadmin')],
    canActivate: [authGuard],
    loadComponent: () => import('./layout/admin-shell/admin-shell').then((m) => m.AdminShell),
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'dashboard' },
      {
        path: 'dashboard',
        title: 'Admin Dashboard',
        loadComponent: () =>
          import('./features/admin/dashboard/dashboard').then((m) => m.AdminDashboard),
      },
      {
        path: 'clinics',
        title: 'Clinic Management',
        loadComponent: () => import('./features/admin/clinics/clinics').then((m) => m.Clinics),
      },
      {
        path: 'roles',
        title: 'Role Management',
        loadComponent: () => import('./features/admin/roles/roles').then((m) => m.Roles),
      },
      {
        // Shown as "Subscriptions"; the path stays /plans so existing links keep working.
        path: 'plans',
        title: 'Subscriptions',
        loadComponent: () => import('./features/admin/plans/plans').then((m) => m.Plans),
      },
      {
        path: 'facescan',
        title: 'Face Scan Analytics',
        loadComponent: () => import('./features/admin/facescan/facescan').then((m) => m.FaceScan),
      },
      {
        path: 'statistics',
        title: 'Statistics',
        loadComponent: () =>
          import('./features/admin/statistics/statistics').then((m) => m.Statistics),
      },
      {
        path: 'architecture',
        title: 'Platform Architecture',
        canActivate: [superAdminGuard],
        loadComponent: () =>
          import('./features/admin/architecture/architecture').then((m) => m.Architecture),
      },
      {
        path: 'system-health',
        title: 'System Health',
        loadComponent: () =>
          import('./features/admin/system-health/system-health').then((m) => m.SystemHealth),
      },
      // Clinic Admins assigned other companies to an owner, which the multi-clinic
      // plan rules out (admins stay inside their own company). Hidden until phase 1
      // retires it with clinic_admin_assignments; old links land on the dashboard.
      { path: 'admins', redirectTo: 'dashboard' },
    ],
  },
  // Admin — clinic owners (users_tb.user_type_id = 2), managing their own clinics.
  {
    path: '',
    canMatch: [portalMatch('admin')],
    canActivate: [authGuard],
    loadComponent: () => import('./layout/admin-shell/admin-shell').then((m) => m.AdminShell),
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'dashboard' },
      {
        // The super-admin Statistics page, scoped to the owner's own clinics.
        path: 'dashboard',
        title: 'Admin Dashboard',
        providers: OWNER_STATISTICS_PROVIDERS,
        loadComponent: () =>
          import('./features/admin/statistics/statistics').then((m) => m.Statistics),
      },
      {
        path: 'clinics',
        title: 'My Clinics',
        loadComponent: () => import('./features/owner/clinics/clinics').then((m) => m.OwnerClinics),
      },
      {
        // One clinic's dashboard; every call is scope-checked server-side.
        path: 'clinics/:companyId',
        title: 'Clinic dashboard',
        loadComponent: () =>
          import('./features/owner/clinic-dashboard/clinic-dashboard').then((m) => m.OwnerClinicDashboard),
      },
      {
        // One clinic's settings; every call is scope-checked server-side.
        path: 'clinics/:companyId/settings',
        title: 'Clinic settings',
        loadComponent: () =>
          import('./features/owner/clinic-settings/clinic-settings').then((m) => m.OwnerClinicSettings),
      },
    ],
  },
  { path: '**', redirectTo: '' },
];

export const routes: Routes = environment.platform === 'admin' ? adminRoutes : crmRoutes;
